import { useState, useEffect, useRef, useMemo } from "react";
import {
  Drawer,
  Modal,
  Skeleton,
  Grid,
  Tabs,
  Button,
  Group,
  Space,
  Box,
  Text,
  TextInput,
  Collapse,
  Divider,
  ScrollArea,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { DatePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { useHotkeys } from "@mantine/hooks";
import {
  IconCheck,
  IconSettings,
  IconExclamationMark,
  IconMessageCircle,
  IconCoin,
  IconAlertCircle,
} from "@tabler/icons";
import { useLocalStorage } from "@mantine/hooks";

import { useServerApi } from "./userServerApi";
import FormSection from "../components/formSection";
import FormTab from "../components/formTab";
import FormWizard from "../components/formWizard";
import { normalizeExtraFieldValuesFromApi } from "../helpers/extraFieldValues";
import { normalizeOrderArFormAccount } from "../helpers/arFormUtils";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../components/pageList";
import ReactJson from "react-json-view";
import useDeepCompareEffect from "./useDeepCompareEffect";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTabNavigate } from "./useTabNavigate";
import { useTabActionsOptional } from "../context/tabContext";
import {
  getEntityTabLabel,
  buildTabKey,
  normalizePathname,
} from "../utils/tabNavigation";
import { useAccessRight } from "./useAccessRight";
import { withDefaultIdIssueCountry } from "../helpers/clientIdIssueCountry";
import { hydrateClientContacts } from "../helpers/clientContactUtils";
import {
  hydrateClientNameFields,
  prepareClientNameForSave,
} from "../helpers/clientNameUtils";
import { hydrateUserContacts } from "../helpers/userContactUtils";
import { MCA_ENTITIES } from "../data/mcaEntities";
import { McaEntityBanner } from "../components/mca/McaEntityIndicators";
import {
  isMcaIntercepted,
  getMcaSubmittedNotificationTitle,
  getMcaSubmittedNotificationMessage,
  isEntityMcaLocked,
} from "../components/mca/mcaApprovalLabels";

import "dayjs/locale/en";
import "dayjs/locale/zh-tw";
import "dayjs/locale/zh-cn";

const _ = require("lodash");

const K_SECTION_MIN_HEIGHT = "350px";
const K_CONFIRM_DIRTY_EXIT =
  "Are you sure to close the form? There are unsaved changes.";

const MCA_API_ENTITIES = new Set(MCA_ENTITIES.map((entity) => entity.value));

/**
 * @descirption: Hook functions useFormRender
 * @Params: [
 *
 *  formSchema:         JSON base schema to describe and build the form
 *  onSumitSuccess:     Function to call when form submit successfully
 *  onSubmit:           Custom submit function instead of using API
 *
 * ]
 *
 */

export const useFormRender = (
  formSchema,
  onSubmitSuccess,
  onSubmit,
  onChange,
) => {
  const [formOpened, setFormOpened] = useState(false);
  const [formSettled, setFormSettled] = useState(false);
  const [formMode, setformMode] = useState("add");
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(null);
  const [addSuccessPromptData, setAddSuccessPromptData] = useState(null);
  const [, setUniqueCheckTick] = useState(0);
  const [api] = useServerApi();
  const navigate = useNavigate();
  const tabNav = useTabNavigate();
  const tabActions = useTabActionsOptional();
  const [currentFormSchema, setCurrentFormSchema] = useState({});
  const location = useLocation();
  /** Avoid redundant clear/load/navigate when LINK URL sync re-runs for the same record. */
  const linkSessionRef = useRef({ id: null, mode: null });
  const formRef = useRef(null);
  const loadGenerationRef = useRef(0);
  const userEditedRef = useRef(false);
  const loadedOnceRef = useRef(null);
  const skipListNotifyOnLoadRef = useRef(false);

  const markUserEdited = () => {
    userEditedRef.current = true;
  };

  const resetUserEdited = () => {
    userEditedRef.current = false;
  };

  const hasUnsavedUserChanges = () => userEditedRef.current;

  const query = new URLSearchParams(useLocation().search);
  const linkId = query.get("id");
  const linkMode = query.get("mode");
  const linkPath = useLocation().pathname;
  const { t, i18n } = useTranslation();
  const [getAccessRight] = useAccessRight();
  const accessRight = getAccessRight();

  /**
   * HotKeys Setting
   */
  useHotkeys([
    // ["ctrl+S", () => saveForm()],
    // ["mod+S", () => saveForm()],
  ]);

  /**
   * Definition of possible Form Action which will be accesible through hook
   */
  const formAction = {
    open: (o) => openForm(o),

    close: (warning = false) => closeForm(warning),
    save: () => saveForm(),
    load: (data) => loadData(data),
    setFieldValue: (field, value) => setFieldValue(field, value),
    updateAndSave: (updates) => updateAndSave(updates),
    getTitle: () => getFormTitle(),
    getValues: () => form.values,
    clear: () => clearForm(),
    setMode: (mode) => setformMode(mode),
    getCurrentSchema: () => currentFormSchema,
    reload: () => loadFormData(id),
    notifyListRefresh: () => notifySiblingListTabsIfNeeded(),
  };

  /**
   * Description: Get the current form schema. Schema define the layout and fields
   * of the form. Form definition may be same for edit mode and add mode. If form structure
   * are different for modes, further definition of schema fields (add , edit) need.
   * @returns
   */
  const getCurrentFormSchema = () =>
    (formMode === "add" ? formSchema.add : formSchema.edit) ?? formSchema;

  const getCurrentFormSchemByMode = (mode) =>
    (mode === "add" ? formSchema.add : formSchema.edit) ?? formSchema;

  /**
   * display:     the definition of display ways of forms (ENUM_FORM_DISPLAY)
   * showSaveBar: whether to show the save and close menu bar on the top of the form
   */
  const {
    display,
    showSaveBar = true,
    showSaveButton = false,
    saveButtonText = "Save",
    apiEntity,
    saveWithNotification = true,
    name: schemaName,
    initialValues,
  } = getCurrentFormSchema();

  /**
   * definition of the form object by using the hook useForm from mantine
   */
  // LINK full-page forms re-render heavily on mobile; validating every keystroke
  // re-renders the entire tree (tabs, grids, ReactQuill) and causes visible flicker.
  const formUsesLinkDisplay =
    formSchema.add?.display?.mode === ENUM_FORM_DISPLAY.LINK ||
    formSchema.edit?.display?.mode === ENUM_FORM_DISPLAY.LINK;

  const notifySiblingListTabsIfNeeded = () => {
    if (!tabActions?.enabled || !formUsesLinkDisplay) return;
    if (!linkId && linkMode !== "add") return;
    tabActions.notifyListRefresh(linkPath);
  };

  useEffect(() => {
    loadedOnceRef.current = null;
  }, [linkId]);
  const form = useForm({
    initialValues: getCurrentFormSchema().initialValues,
    validate: getCurrentFormSchema().validate,
    validateInputOnChange: !formUsesLinkDisplay,
  });
  formRef.current = form;

  const isMcaFormLocked = () => {
    const entityKey = getCurrentFormSchema().apiEntity;
    if (formMode !== "edit" || !entityKey || !MCA_API_ENTITIES.has(entityKey)) {
      return false;
    }
    return isEntityMcaLocked(form.values);
  };

  const notifyMcaLocked = () => {
    showNotification({
      color: "orange",
      icon: <IconAlertCircle size={18} />,
      title: "Changes blocked",
      message:
        "This record has a pending MCA approval. Use the MCA request to revise or cancel it.",
    });
  };

  const markFormUnsettled = () => {
    loadGenerationRef.current += 1;
    setFormSettled(false);
    resetUserEdited();
  };

  const syncDirtyBaselineAfterLoad = () => {
    const generation = ++loadGenerationRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (loadGenerationRef.current !== generation) return;
        const currentForm = formRef.current;
        if (!currentForm) return;
        if (!userEditedRef.current) {
          currentForm.resetDirty(currentForm.values);
        }
        setFormSettled(true);
      });
    });
  };

  const applyLoadedFormData = (data) => {
    let merged = _.merge({}, getCurrentFormSchema().initialValues, data);
    if (getCurrentFormSchema().apiEntity === "client") {
      merged = withDefaultIdIssueCountry(merged);
      merged = hydrateClientContacts(merged);
      merged = hydrateClientNameFields(merged);
    }
    if (getCurrentFormSchema().apiEntity === "user") {
      merged = hydrateUserContacts(merged);
    }
    if (merged.extraFieldValues != null) {
      merged.extraFieldValues = normalizeExtraFieldValuesFromApi(
        merged.extraFieldValues,
      );
    }
    if (getCurrentFormSchema().apiEntity === "orderServiceType") {
      merged.fields = Array.isArray(merged.fields) ? merged.fields : [];
      merged.completeOrderUpdates = Array.isArray(merged.completeOrderUpdates)
        ? merged.completeOrderUpdates
        : [];
    }
    if (getCurrentFormSchema().apiEntity === "order") {
      merged.arForm = normalizeOrderArFormAccount(merged.arForm);
    }
    form.setValues(merged);
    if (!userEditedRef.current) {
      form.resetDirty(merged);
    }
    syncDirtyBaselineAfterLoad();
  };

  const ownerTabId = useMemo(() => {
    if (!tabActions?.enabled) return null;
    const tabs = tabActions.getTabs();
    if (!tabs.length) return null;

    if (linkId) {
      const key = buildTabKey(linkPath, location.search);
      return tabs.find((tab) => tab.key === key)?.id ?? null;
    }

    const path = normalizePathname(linkPath);
    return tabs.find((tab) => tab.key === path)?.id ?? null;
  }, [tabActions, linkId, linkPath, location.search]);

  useEffect(() => {
    if (!form || !onChange) return;
    onChange(form.values);
  }, [form.values]);

  useEffect(() => {
    if (!tabActions?.enabled || !ownerTabId) return;

    if (!formOpened || !formSettled) {
      tabActions.registerDirtyCheck(ownerTabId, null);
      return;
    }

    tabActions.registerDirtyCheck(ownerTabId, () => hasUnsavedUserChanges());
    return () => tabActions.registerDirtyCheck(ownerTabId, null);
  }, [formOpened, formSettled, tabActions, ownerTabId]);

  useEffect(() => {
    const schema = formSchema.edit ?? formSchema;
    if (!schema?.display || schema.display.mode !== ENUM_FORM_DISPLAY.LINK)
      return;

    if (!linkId) {
      if (linkMode === "add") {
        const sameSession =
          linkSessionRef.current.id === null &&
          linkSessionRef.current.mode === "add";

        if (!sameSession) {
          markFormUnsettled();
          resetUserEdited();
          linkSessionRef.current = { id: null, mode: "add" };
          const schema = formSchema.add ?? formSchema;
          const initial = schema.initialValues ?? {};
          form.reset();
          form.setValues(initial);
          form.resetDirty(initial);
          syncDirtyBaselineAfterLoad();
        } else {
          setFormSettled(true);
        }

        setId(null);
        setformMode("add");
        setFormOpened(true);
        return;
      }

      linkSessionRef.current = { id: null, mode: null };
      setFormOpened(false);
      setFormSettled(false);
      return;
    }

    if (!linkMode) {
      navigate(`${location.pathname}?id=${linkId}&mode=edit`, {
        replace: true,
      });
      return;
    }

    const effectiveMode = linkMode || "edit";
    const sameRecord =
      linkSessionRef.current.id === linkId &&
      linkSessionRef.current.mode === effectiveMode;

    if (!sameRecord) {
      markFormUnsettled();
      clearForm();
      linkSessionRef.current = { id: linkId, mode: effectiveMode };
      loadFormData(linkId);
    } else {
      setFormSettled(true);
    }

    setId(linkId);
    setformMode(effectiveMode);
    setFormOpened(true);
  }, [linkId, linkMode]);

  /**
   * Form Action Functions
   *
   */
  const saveForm = async () => {
    try {
      if (onSubmit) {
        return await saveWithCustom();
      }
      return await save();
    } catch (error) {
      console.log(error);
    }
  };

  const saveWithCustom = async () => {
    try {
      if (formMode === "show") return;
      if (isMcaFormLocked()) {
        notifyMcaLocked();
        return;
      }

      const validation = form.validate();
      if (validation.hasErrors) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Validation Error",
          message: "Please check the form for errors.",
        });
        return;
      }

      let values = mergeQuietFieldSnapshot(form.values);
      if (apiEntity === "client") {
        values = prepareClientNameForSave(values);
      }
      setLoading(true);
      const result = await onSubmit({ values, formMode });
      setLoading(false);

      if (result?.cancelled) return result;

      if (result && result.success === false) {
        if (result.error) return notifySaveFail(result.error);
        return result;
      }

      if (isMcaIntercepted(result)) {
        notifyMcaSubmitted(result);
        if (formMode === "edit" && id) {
          skipListNotifyOnLoadRef.current = true;
          try {
            await loadFormData(id);
          } finally {
            skipListNotifyOnLoadRef.current = false;
          }
        }
        return handleEditSuccess(result);
      }

      if (formMode === "add" && result?.success) {
        notifySaveOk(
          `${values.name ?? values.code ?? "Data"} add successfully`,
        );
        handleAddSuccess(result);
        return result;
      }

      if (formMode === "edit" && id && result?.success !== false) {
        notifySaveOk(`${values.name ?? values.code ?? "Data"} Updated`);
        skipListNotifyOnLoadRef.current = true;
        try {
          await loadFormData(id);
        } finally {
          skipListNotifyOnLoadRef.current = false;
        }
        return handleEditSuccess(result);
      }

      if (onSubmitSuccess) {
        onSubmitSuccess({ values, result });
        if (formSchema.closeAfterSave) closeForm();
      }
      return result;
    } catch (error) {
      setLoading(false);
      console.log(error);
      return notifySaveFail(error);
    }
  };

  const save = async () => {
    try {
      if (formMode === "show") return;
      if (isMcaFormLocked()) {
        notifyMcaLocked();
        return;
      }

      const validation = form.validate();
      if (validation.hasErrors) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Validation Error",
          message: "Please check the form for errors.",
        });
        return;
      }

      if (formMode === "add") {
        await saveNew();
      }

      if (formMode === "edit") {
        await saveEdit();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const saveNew = async () => {
    try {
      // console.log("Save New", formMode);
      if (!saveWithNotification) return;

      //API Submit
      setLoading(true);
      if (!apiEntity) return;
      let values = form.values;
      if (apiEntity === "client") {
        values = prepareClientNameForSave(values);
      }
      const result = await api.add({ apiEntity, values });
      // console.log("Save  New", result);

      setLoading(false);
      //Handle Fail Add
      if (!result.success) {
        return notifySaveFail(result.error);
      }

      if (isMcaIntercepted(result)) {
        notifyMcaSubmitted(result);
        handleAddSuccess(result);
        return result;
      }

      notifySaveOk(`${values.name ?? values.code ?? "Data"} add successfully`);
      handleAddSuccess(result);
    } catch (error) {
      console.log(error);
    }
  };

  const notifySaveFail = (error) => {
    try {
      console.log(error);
      return showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Save Fail",
        message: api.getErrorMsg(error),
        // message: _.isString(error) ? error : error?.message ?? error?.msg,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const notifySaveOk = (title) => {
    try {
      if (!saveWithNotification) return;
      return showNotification({
        title,
        // icon: <IconCheck size={18} />,
        message: "Data Saved",
      });
    } catch (error) {
      console.log(error);
    }
  };

  const notifyMcaSubmitted = (result) => {
    try {
      if (!saveWithNotification) return;
      const requestId = result?.data?.mcaRequest?._id;
      const message = getMcaSubmittedNotificationMessage(result);
      return showNotification({
        color: "orange",
        icon: <IconAlertCircle size={18} />,
        title: getMcaSubmittedNotificationTitle(),
        message: requestId ? (
          <>
            {message}{" "}
            <Text
              component={Link}
              to={`/mca/requests/${requestId}`}
              size="xs"
              color="blue"
            >
              View request
            </Text>
          </>
        ) : (
          message
        ),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getAddSuccessData = (result) => {
    if (isMcaIntercepted(result)) {
      const doc = result.data?.doc;
      if (doc) {
        return {
          ...doc,
          mcaRequestId: result.data.mcaRequest ?? doc.mcaRequestId,
        };
      }
    }
    return result.data;
  };

  const navigateToCreatedRecord = (data) => {
    const editSchema = getCurrentFormSchemByMode("edit");
    setformMode("edit");
    setId(data._id);
    loadFormData(data._id);

    if (editSchema?.display?.mode === ENUM_FORM_DISPLAY.LINK) {
      linkSessionRef.current = { id: null, mode: null };
      setFormOpened(false);
      if (tabNav.enabled) {
        tabNav.openDetail({
          pathname: location.pathname,
          id: data._id,
          mode: "edit",
          label: getEntityTabLabel(
            data,
            data._id,
            getCurrentFormSchemByMode("edit").apiEntity,
          ),
        });
        return;
      }
      navigate(`${location.pathname}?id=${data._id}&mode=edit`);
    }
  };

  const handleAddSuccess = (result) => {
    // console.log("handleAddSuccess", result);
    try {
      const data = getAddSuccessData(result);
      notifySiblingListTabsIfNeeded();
      const addSchema = getCurrentFormSchema();
      const editSchema = getCurrentFormSchemByMode("edit");

      if (
        addSchema?.promptAfterAdd &&
        editSchema?.display?.mode === ENUM_FORM_DISPLAY.LINK
      ) {
        linkSessionRef.current = { id: null, mode: null };
        setFormOpened(false);
        clearForm();
        if (onSubmitSuccess) onSubmitSuccess({ values: data });
        setAddSuccessPromptData(data);
        return;
      }

      setformMode("edit");
      setId(data._id);
      loadFormData(data._id);

      if (editSchema?.display?.mode === ENUM_FORM_DISPLAY.LINK) {
        linkSessionRef.current = { id: null, mode: null };
        setFormOpened(false);
        if (onSubmitSuccess) onSubmitSuccess({ values: data });
        if (tabNav.enabled) {
          tabNav.openDetail({
            pathname: location.pathname,
            id: data._id,
            mode: "edit",
            label: getEntityTabLabel(
              data,
              data._id,
              getCurrentFormSchema().apiEntity,
            ),
          });
          return;
        }
        const link = `${location.pathname}?id=${data._id}&mode=edit`;
        navigate(link);
        return;
      }

      if (addSchema.closeAfterSave || formSchema.closeAfterSave) closeForm();

      if (onSubmitSuccess) onSubmitSuccess({ values: data });
    } catch (error) {
      console.log(error);
    }
  };

  const mergeQuietFieldSnapshot = (values) => ({
    ...values,
    ...(form._quietFieldSnapshot || {}),
  });

  const handleEditSuccess = (result, options = {}) => {
    const { quiet = false } = options;
    notifySiblingListTabsIfNeeded();
    if (quiet) return result;
    if (getCurrentFormSchema().closeAfterSave || formSchema.closeAfterSave)
      closeForm();
    if (onSubmitSuccess) return onSubmitSuccess({ values: form.values });
    return result;
  };

  /**
   * Description : Save to server with the form values
   */
  const saveEdit = async () => {
    await saveValues(mergeQuietFieldSnapshot(form.values));
  };

  /**
   * @description Save to server with the bypass values
   * @param {*} values
   * @returns
   */
  const saveValues = async (values, options = {}) => {
    const { quiet = false, skipFormUpdate = false } = options;
    if (isMcaFormLocked()) {
      if (!quiet) notifyMcaLocked();
      return;
    }
    // console.log("saveValues", form.values, apiEntity, id);
    if (!apiEntity || !id) return;

    let payload = mergeQuietFieldSnapshot(values);
    if (apiEntity === "client") {
      payload = prepareClientNameForSave(payload);
    }

    if (!quiet) {
      const isExist = await api.getById({ apiEntity, id });
      if (!isExist) return;
    }

    if (!quiet) setLoading(true);
    let result = await api.updateById({ apiEntity, values: payload, id });
    if (!quiet) setLoading(false);
    if (!result.success) {
      // console.log(result);
      return notifySaveFail(result.error);
    }

    if (isMcaIntercepted(result)) {
      if (!quiet) notifyMcaSubmitted(result);
      if (formMode === "edit" && id) {
        skipListNotifyOnLoadRef.current = true;
        try {
          await loadFormData(id);
        } finally {
          skipListNotifyOnLoadRef.current = false;
        }
      }
      return handleEditSuccess(result, options);
    }

    if (!quiet) {
      notifySaveOk(`${payload.name ?? payload.code ?? "Data"} Updated`);
      if (formMode === "edit" && id) {
        skipListNotifyOnLoadRef.current = true;
        try {
          await loadFormData(id);
        } finally {
          skipListNotifyOnLoadRef.current = false;
        }
      }
    }
    return handleEditSuccess(result, options);
  };

  /**
   * @description update the form value and save to db directly
   * @param {*} field
   * @param {*} value
   */
  const updateAndSave = async (updates, options = {}) => {
    const { quiet = false, skipFormUpdate = false } = options;
    // console.log("updateAndSave", updates);
    if (!skipFormUpdate) {
      for (const [field, value] of Object.entries(updates)) {
        form.setFieldValue(field, value);
      }
    } else if (form._quietFieldSnapshot) {
      form._quietFieldSnapshot = { ...form._quietFieldSnapshot, ...updates };
    } else {
      form._quietFieldSnapshot = { ...updates };
    }
    const v = mergeQuietFieldSnapshot({ ...form.values, ...updates });
    // console.log("updateAndSave v", v);
    await saveValues(v, options);
  };

  const clearForm = () => {
    form.reset();
    setformMode(null);
    setId(null);
  };

  const loadData = (data) => {
    return applyLoadedFormData(data);
  };

  const setFieldValue = (field, value) => {
    return form.setFieldValue(field, value);
  };

  const openForm = ({ mode, id, data }) => {
    try {
      const normalizedMode = mode || (id ? "edit" : "add");
      const schema = getCurrentFormSchemByMode(normalizedMode);

      if (schema?.display?.mode === ENUM_FORM_DISPLAY.LINK) {
        const linkSearch = id
          ? `?id=${id}&mode=${normalizedMode}`
          : `?mode=${normalizedMode}`;

        if (tabNav.enabled) {
          tabNav.openDetail({
            pathname: location.pathname,
            id,
            mode: normalizedMode,
            label: id?.slice?.(-8),
          });
          return;
        }

        const cur = new URLSearchParams(location.search);
        const curMode = cur.get("mode") || "edit";
        const curId = cur.get("id");
        const alreadyThere =
          curId === (id ?? null) &&
          curMode === normalizedMode &&
          linkSessionRef.current.id === (id ?? null) &&
          linkSessionRef.current.mode === normalizedMode;

        if (alreadyThere) {
          setId(id ?? null);
          setformMode(normalizedMode);
          setFormOpened(true);
          if (id) loadFormData(id);
          return;
        }

        window.scrollTo(0, 0);
        navigate(`${location.pathname}${linkSearch}`);
        return;
      }

      clearForm();
      setformMode(mode);
      setId(null);
      setFormOpened(true);

      //Open new tab
      if (schema?.display?.mode === ENUM_FORM_DISPLAY.NEWTAB) {
        if (tabNav.enabled) {
          return tabNav.openDetail({
            pathname: schema?.display?.entityLink,
            id,
            mode: normalizedMode,
          });
        }
        const link = `${schema?.display?.entityLink}?id=${id}&mode=${mode}`;
        return window.open(link, "_blank");
      }

      if (data) {
        // console.log("openForm", data);
        if (id) setId(id);
        return applyLoadedFormData(data);
      }

      if (id) {
        setId(id);
        loadFormData(id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const closeForm = (warning) => {
    // console.log("Close Form");
    try {
      if (
        warning &&
        hasUnsavedUserChanges() &&
        !window.confirm(K_CONFIRM_DIRTY_EXIT)
      )
        return false;

      setFormOpened(false);
      setFormSettled(false);
      resetUserEdited();
      clearForm();
      if (formSetting.displayMode === ENUM_FORM_DISPLAY.LINK) {
        linkSessionRef.current = { id: null, mode: null };
        if (tabNav.enabled) {
          let survivorTabId = null;
          const activeTabId = tabActions?.getActiveTabId();
          if (activeTabId && (linkId || linkMode === "add")) {
            const listKey = buildTabKey(normalizePathname(linkPath), "");
            const listTab = tabActions
              .getTabs()
              .find((tab) => tab.key === listKey && tab.id !== activeTabId);
            survivorTabId = listTab?.id ?? null;
          }
          tabNav.closeActive({ skipDirtyCheck: true, survivorTabId });
          return true;
        }
        if (window.opener != null) {
          try {
            window.close();
          } catch (e) {
            navigate(linkPath);
          }
        } else {
          navigate(linkPath);
        }
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const loadFormData = async (id) => {
    markFormUnsettled();
    const isReload =
      !!linkId &&
      loadedOnceRef.current === linkId &&
      !skipListNotifyOnLoadRef.current;
    try {
      // console.log("loadFormData", id, getCurrentFormSchema().apiEntity);
      if (!id) {
        syncDirtyBaselineAfterLoad();
        return;
      }
      const tabKey =
        tabActions?.enabled && linkId
          ? buildTabKey(linkPath, location.search)
          : null;
      let data = await api.getById({
        apiEntity: getCurrentFormSchema().apiEntity,
        id,
      });
      form._quietFieldSnapshot = null;
      form._reloadKey = (form._reloadKey || 0) + 1;
      applyLoadedFormData(data);
      if (linkId) {
        loadedOnceRef.current = linkId;
      }
      if (isReload) {
        notifySiblingListTabsIfNeeded();
      }

      if (tabKey) {
        const label = getEntityTabLabel(
          data,
          id,
          getCurrentFormSchema().apiEntity,
        );
        tabActions.updateTabLabelByKey(tabKey, label);
      }
      // console.log("loadFormData", data);
    } catch (error) {
      console.log(error);
      syncDirtyBaselineAfterLoad();
    }
  };

  const onCloseForm = () => {
    // if (
    //   formMode === "add" &&
    //   form.isDirty() &&
    //   !window.confirm(K_CONFIRM_DIRTY_EXIT)
    // )
    //   return;

    setFormOpened(false);
    form.reset();
  };

  const getFormTitle = () => {
    try {
      const { title, titleField } = getCurrentFormSchema();

      if (formMode === "add") return t(title);

      let str = "";
      if (title) str += t(title);
      if (title && titleField) str += " > ";
      if (titleField) str += _.get(form.values, titleField) ?? "";

      return str;
    } catch (error) {
      console.log(error);
    }
  };

  const getChildContainers = (key) =>
    getCurrentFormSchema().layout.containers?.filter((c) => c.parent === key);
  const getChildFields = (key) =>
    getCurrentFormSchema().layout.fields?.filter((c) => c.parent === key);

  const attachFormAction = () => {
    form._uniqueCheckPending = form._uniqueCheckPending || {};
    form.setUniqueCheckPending = (field, pending) => {
      if (pending) {
        form._uniqueCheckPending[field] = true;
      } else {
        delete form._uniqueCheckPending[field];
      }
      setUniqueCheckTick((tick) => tick + 1);
    };
    form.isUniqueCheckPending = () =>
      Object.keys(form._uniqueCheckPending).length > 0;

    form.save = async () => await saveForm();
    form.saveValues = (values) => saveValues(values);
    form.open = (o) => openForm(o);
    // form.updateAndSave = (field, value) => updateAndSave(field, value);
    form.updateAndSave = (updates) => updateAndSave(updates);
    form.persistQuiet = (updates) =>
      updateAndSave(updates, { quiet: true, skipFormUpdate: true });
    form.close = (warning = false) => closeForm(warning);
    form.markUserEdited = markUserEdited;
    form.isMcaLocked = isMcaFormLocked;
    form.getTitle = () => getFormTitle();
    form.clear = () => clearForm();
    form.reload = () => loadFormData(id);
    form.notifyListRefresh = () => notifySiblingListTabsIfNeeded();
  };

  const renderField = (field) => {
    try {
      let value = _.get(form.values, field.name);
      if (field.type === "datePicker" && value) {
        value = new Date(value);
        field.props.locale = i18n.language === "en" ? "en" : "zh-tw";
      }
      const originalOnChange = form.getInputProps(field.name).onChange;

      const visible = !_.isFunction(field.visibleOnly)
        ? true
        : field.visibleOnly(form.values, accessRight);

      const isShowModeReadOnly =
        (formMode === "show" && !field.props?.editableInShowMode) ||
        (isMcaFormLocked() && !field.props?.editableDuringMcaApproval);
      const isEmptyValue =
        value === null || value === undefined || value === "";

      if (isShowModeReadOnly && isEmptyValue && field.type === "datePicker") {
        return (
          <>
            {visible && (
              <span key={`${field.parent}-${field.name}`}>
                {field.props?.label && (
                  <Text size="xs" color="dimmed">
                    {t(field.props.label)}
                  </Text>
                )}
                <Text size="sm" weight={600}>
                  -
                </Text>
                <Space h={field.fieldSpacing ?? "md"} />
              </span>
            )}
          </>
        );
      }

      const displayValue = isShowModeReadOnly && isEmptyValue ? "-" : value;

      const fieldRequired = _.isFunction(field.props?.required)
        ? field.props.required(form.values, accessRight)
        : field.props?.required;

      return (
        <>
          {/* {i18n.language} */}
          {visible && (
            <>
              <span key={`${field.parent}-${field.name}`}>
                <field.component
                  disabled={isShowModeReadOnly}
                  readOnly={
                    Boolean(field.props?.readOnly) ||
                    (isShowModeReadOnly && !field.props?.editableInShowMode)
                  }
                  variant={isShowModeReadOnly ? "unstyled" : "default"}
                  style={field.style}
                  key={`${field.parent}-${field.name}`}
                  name={field.name}
                  {...(field.props ?? {})}
                  {...form.getInputProps(field.name, {
                    type: field?.props?.type,
                  })}
                  required={isShowModeReadOnly ? false : fieldRequired}
                  withAsterisk={
                    isShowModeReadOnly
                      ? false
                      : (field.props?.withAsterisk ?? fieldRequired)
                  }
                  value={displayValue}
                  form={form} //For custom component
                  formAction={formAction}
                  formMode={formMode}
                  locale={i18n.language}
                  placeholder={
                    field.props?.placeholder
                      ? t(field.props.placeholder)
                      : undefined
                  }
                  label={field.props?.label ? t(field.props.label) : undefined}
                  onChange={(value) => {
                    markUserEdited();
                    originalOnChange(value);
                    if (field.props?.onDateChange) {
                      field.props.onDateChange(value, form);
                    }
                  }}
                />
                <Space h={field.fieldSpacing ?? "md"} />
              </span>
            </>
          )}
        </>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderFields = (fields) => fields?.map((f) => renderField(f));

  const renderContainerSection = (section) => {
    try {
      return (
        <FormSection
          key={section.key}
          mt={section.props?.mt || "xl"}
          style={{ minHeight: K_SECTION_MIN_HEIGHT }}
          {...section.props}
        >
          {getChildContainers(section.key).map((c) => renderContainer(c))}
          {getChildFields(section.key).map((f) => renderField(f))}
        </FormSection>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderContainerTabs = (tabs, form) => {
    try {
      return (
        <FormTab
          key={tabs.key}
          tabs={tabs}
          getChildContainers={getChildContainers}
          getChildFields={getChildFields}
          renderContainer={renderContainer}
          renderField={renderField}
          form={form}
        ></FormTab>
      );
    } catch (error) {
      console.log(error);
    }
  };
  const renderContainerWizard = (wizard, form) => {
    try {
      return (
        <>
          <FormWizard
            key={wizard.key}
            wizard={wizard}
            getChildContainers={getChildContainers}
            getChildFields={getChildFields}
            renderContainer={renderContainer}
            renderField={renderField}
            form={form}
            confirmSubmit={async () => {
              await saveForm();
              // closeForm();
            }}
          />
        </>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderContainerGrid = (container) => {
    try {
      return (
        <Grid key={container.key} {...container.props} gutter="xl">
          {container.cols.map(({ props, key }) => (
            <Grid.Col {...props} key={key}>
              {getChildContainers(key).map((c) => renderContainer(c))}
              {getChildFields(key).map((f) => renderField(f))}
            </Grid.Col>
          ))}
        </Grid>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderContainerBox = (container) => {
    try {
      return (
        <Box key={container.key} {...container.props}>
          <>
            {getChildContainers(container.key).map((c) => renderContainer(c))}
            {getChildFields(container.key).map((f) => renderField(f))}
          </>
        </Box>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderContainerEmpty = (container) => {
    try {
      return (
        <>
          {getChildContainers(container.key).map((c) => renderContainer(c))}
          {getChildFields(container.key).map((f) => renderField(f))}
        </>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderContainer = (container) => {
    try {
      if (_.isFunction(container.visibleOnly)) {
        if (!container.visibleOnly(form.values, accessRight)) return <></>;
      }

      switch (container.type) {
        case ENUM_FORM_LAYOUT_CONTAINER.EMPTY:
          return renderContainerEmpty(container);
        case ENUM_FORM_LAYOUT_CONTAINER.BOX:
          return renderContainerBox(container);
        case ENUM_FORM_LAYOUT_CONTAINER.GRID:
          return renderContainerGrid(container);
        case ENUM_FORM_LAYOUT_CONTAINER.TABS:
          return renderContainerTabs(container, form);
        case ENUM_FORM_LAYOUT_CONTAINER.WIZARD:
          return renderContainerWizard(container, form);
        case ENUM_FORM_LAYOUT_CONTAINER.SECTION:
          return renderContainerSection(container);
      }
      return <> No Container Type Defined</>;
    } catch (error) {
      console.log(error);
    }
  };

  const renderFormMain = () => {
    try {
      attachFormAction();
      const { layout } = getCurrentFormSchema();
      if (!layout) return <>Form Layout Not Defined</>;

      const formBody =
        !layout.containers || layout.containers.length == 0
          ? renderFields(layout.fields)
          : layout.containers
              .filter((c) => c.parent === null)
              .map((container) => renderContainer(container));

      return (
        <>
          {formMode === "edit" &&
            apiEntity &&
            MCA_API_ENTITIES.has(apiEntity) && (
              <McaEntityBanner entity={form.values} />
            )}
          {formBody}
        </>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderSaveBar = () => {
    return (
      <>
        <Group position="apart">
          <span></span>

          <Group>
            {formMode !== "show" && !isMcaFormLocked() && (
              <Button
                size="xs"
                variant="filled"
                onClick={saveForm}
                loading={loading}
              >
                {t(saveButtonText)}
              </Button>
            )}
            <Button
              size="xs"
              variant="default"
              onClick={() => closeForm(true)}
              disabled={loading}
            >
              {t("Close")}
            </Button>
          </Group>
        </Group>
      </>
    );
  };

  const renderAddSuccessPrompt = () => {
    if (!addSuccessPromptData) return null;

    const { code, name } = addSuccessPromptData;
    const label = [code, name].filter(Boolean).join(" — ");

    return (
      <Modal
        opened
        onClose={() => setAddSuccessPromptData(null)}
        title={t("Client Created")}
        closeOnClickOutside={false}
        closeOnEscape={false}
        size="sm"
      >
        <Text>
          {label
            ? t("{{label}} has been created successfully.", { label })
            : t("Client has been created successfully.")}
        </Text>
        <Text size="sm" color="dimmed" mt="sm">
          {t("What would you like to do next?")}
        </Text>
        <Group position="apart" mt="xl">
          <Button
            variant="default"
            onClick={() => {
              const data = addSuccessPromptData;
              setAddSuccessPromptData(null);
              navigateToCreatedRecord(data);
            }}
          >
            {t("View Client")}
          </Button>
          <Button
            onClick={() => {
              setAddSuccessPromptData(null);
              openForm({ mode: "add" });
            }}
          >
            {t("Create Another Client")}
          </Button>
        </Group>
      </Modal>
    );
  };

  const renderForm = () => {
    try {
      return (
        <>
          {renderAddSuccessPrompt()}
          {renderFormBody()}
        </>
      );
    } catch (error) {
      console.log(error);
    }
  };

  const renderFormBody = () => {
    try {
      switch (display?.mode) {
        case ENUM_FORM_DISPLAY.INSIDE:
          return (
            <>
              {/* {<ReactJson src={form} style={{ background: "white" }} collapsed />} */}

              {formOpened && renderFormMain()}
              {showSaveButton && formMode !== "show" && !isMcaFormLocked() && (
                <Button size="xs" variant="filled" onClick={saveForm}>
                  {saveButtonText}
                </Button>
              )}
            </>
          );

        case ENUM_FORM_DISPLAY.LINK:
          return (
            <>
              {/* {
                <ReactJson
                  src={form.values}
                  style={{ background: "white" }}
                  collapsed
                />
              } */}

              {formOpened && renderFormMain()}
              {showSaveButton && formMode !== "show" && !isMcaFormLocked() && (
                <Button size="xs" variant="filled" onClick={saveForm}>
                  {saveButtonText}
                </Button>
              )}
            </>
          );

        case ENUM_FORM_DISPLAY.DRAWER:
          return (
            <Drawer
              opened={formOpened}
              onClose={onCloseForm}
              title={getFormTitle()}
              padding="xl"
              size={display.size}
              position={display.position}
            >
              {/* Drawer content */}
              {/* {formMode} - {id} */}
              {/* { JSON.stringify(form.values)} */}
              <ScrollArea>{renderFormMain()}</ScrollArea>

              {showSaveBar && renderSaveBar()}
            </Drawer>
          );

        case ENUM_FORM_DISPLAY.MODAL:
          return (
            <Modal
              opened={formOpened}
              onClose={onCloseForm}
              title={getFormTitle()}
              size={display.size ?? "xl"}
              closeOnClickOutside={false}
              closeOnEscape={false}
              styles={{
                header: {
                  fontWeight: "bold",
                },
              }}
            >
              {/* Modal content */}
              {/* Mode: {{ formMode } - { id }}
              {JSON.stringify(form.values)} */}
              {/* <ReactJson
                src={form.values}
                style={{ background: "white" }}
                collapsed
              /> */}
              {renderFormMain()}
              <Space h="xl" />
              {showSaveBar && renderSaveBar()}
            </Modal>
          );

        case ENUM_FORM_DISPLAY.FULLSCREEN:
          return (
            <Modal
              zIndex={999}
              opened={formOpened}
              onClose={() => setFormOpened(false)}
              title={getFormTitle()}
              fullScreen
            >
              {/* Modal content */}
              {/* {formMode} - {id} */}
              {showSaveBar && renderSaveBar()}
              {renderFormMain()}
            </Modal>
          );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const formDisplay = display;

  const formStatus = {
    mode: formMode,
    isOpen: formOpened, //isDirty ....
    isMcaLocked: isMcaFormLocked(),
  };

  const formSetting = {
    schema: getCurrentFormSchema(),
    displayMode: display?.mode,
  };

  return [renderForm, formAction, formStatus, formSetting, form];
  // return [ renderForm, formAction, formOpened, formMode , formDisplay ]
};
